import { columnTypes } from "@qdabra/forms-shared/sharePoint";
import { nsDfs } from '@qdabra/forms-shared/xmlNamespaces';
import { isNil, map } from "ramda";

import {
    makeCmeElement,
    makeDElement,
    makeNoNsElement,
    makePcElement,
} from "../../xml/element.builders";
import { buildRichText } from "../../utility/richTextHelpers";
import { setNodeData } from "../../utility/nodeDataHelpers";
import { FILE_ATTACH_URL_KEY } from "../../constants/file.constants";

const {
    SP_TYPE_ATTACHMENTS,
    SP_TYPE_FULLHTML,
    SP_TYPE_MULTI_CHOICE,
    SP_TYPE_LOOKUP_MULTI,
    SP_TYPE_URL,
    SP_TYPE_USER,
    SP_TYPE_USER_MULTI,
} = columnTypes;

const SP_TYPE_COMPATIBLE = 'Compatible';

const setAttachmentFileName = (node, attachment) => {
    const fileName = attachment.substring(attachment.lastIndexOf('/') + 1);

    setNodeData(node, FILE_ATTACH_URL_KEY, fileName);
};

const buildAttachment = (dom, isListForm) => (attachment) => {
    let node = makeNoNsElement(
        dom,
        'attachmentURL',
        { text: attachment },
    );

    if (isListForm) {
        setAttachmentFileName(node, attachment);
    }

    return node;
}

const buildAttachments = (dom, isListForm, attachments) =>
    map(
        buildAttachment(dom, isListForm),
        attachments
    );

const createPersonValueNodes = (dom, { displayName, accountId, accountType }) => [
    makePcElement(dom, 'DisplayName', { text: displayName }),
    makePcElement(dom, 'AccountId', { text: accountId }),
    makePcElement(dom, 'AccountType', { text: accountType || '' }),
];

const createPersonNode = (dom) => (person) =>
    makePcElement(dom, 'Person', {
        children: createPersonValueNodes(dom, person),
    })

const buildPersonNodes = (dom, values) => map(
    createPersonNode(dom),
    values,
);

const buildValueElement = (dom) => (value) =>
    makeNoNsElement(dom, 'Value', { text: value })


const appendValueElements = (dom, values) =>
    map(
        buildValueElement(dom),
        values,
    );

const buildHyperlink = (fieldValue) => ({
    text: fieldValue.url || '',
    attributes: {
        Description: fieldValue.description || '',
    },
});

const buildFieldContents = (dom, isListForm, type, fieldValue) => {
    if (isNil(fieldValue)) {
        return {};
    }

    switch (type) {
        case SP_TYPE_ATTACHMENTS:
            return {
                children: buildAttachments(dom, isListForm, fieldValue),
            };
        case SP_TYPE_USER:
        case SP_TYPE_USER_MULTI:
            return {
                children: buildPersonNodes(dom, fieldValue),
            };
        case SP_TYPE_LOOKUP_MULTI:
        case SP_TYPE_MULTI_CHOICE:
            return {
                children: appendValueElements(dom, fieldValue),
            };
        case SP_TYPE_COMPATIBLE:
        case SP_TYPE_FULLHTML:
            return {
                children: buildRichText(fieldValue),
            };
        case SP_TYPE_URL:
            return buildHyperlink(fieldValue);
        default:
            return { text: (fieldValue ?? '').toString() };
    }
};

const dataFieldNodeCreator = (isListForm) =>
    isListForm
        ? makeCmeElement
        : makeDElement;

const buildDataField = (
    getValue,
    dom,
    isListForm,
    createDataFieldNode,
    item,
) => ({ internalName, type }) => createDataFieldNode(
    dom,
    internalName,
    buildFieldContents(
        dom,
        isListForm,
        type,
        getValue(item, internalName, type),
    ),
);

const buildDataFields = (
    getValue,
    dom,
    isListForm,
    fields,
    createDataFieldNode,
    item,
) => map(
    buildDataField(getValue, dom, isListForm, createDataFieldNode, item),
    fields,
)

const buildDataRow = (
    { getValue },
    dom,
    isListForm,
    fields,
    createDataFieldNode,
    item,
) => createDataFieldNode(dom, "SharePointListItem_RW", {
    children: buildDataFields(
        getValue,
        dom,
        isListForm,
        fields,
        createDataFieldNode,
        item
    ),
});

const buildDataXml = (dom, implementation, isListForm, fields, data) => {
    const createDataFieldNode = dataFieldNodeCreator(isListForm);
    const dataFieldsNode = dom.createElementNS(nsDfs, 'dfs:dataFields');

    const items = data.items;

    for (const item of items) {
        dataFieldsNode.appendChild(buildDataRow(
            implementation,
            dom,
            isListForm,
            fields,
            createDataFieldNode,
            item,
        ));
    }

    return dataFieldsNode;
};

export default buildDataXml;
